const ALLOWED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
];

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

export function validateNewsletterImage(file) {
  if (!file) {
    return "Please select an image";
  }

  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return "Only PNG, JPEG, JPG, WEBP, and GIF images are allowed";
  }

  if (file.size > MAX_IMAGE_SIZE) {
    return "Image size must be under 5 MB";
  }

  return null;
}

// Safely parse a JSON response. If the server returned HTML (nginx error page,
// auth redirect, etc.) we throw a clear error instead of letting JSON.parse blow up.
async function readJsonOrThrow(response, action) {
  const contentType = String(response.headers.get("content-type") || "").toLowerCase();
  const text = await response.text();
  if (!contentType.includes("application/json")) {
    const preview = text.replace(/\s+/g, " ").slice(0, 140);
    throw new Error(
      `${action} failed (HTTP ${response.status}). Server returned non-JSON: ${preview}`,
    );
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`${action} returned invalid JSON (HTTP ${response.status})`);
  }
}

async function directUpload(file) {
  const formData = new FormData();
  formData.append("file", file);
  let response;
  try {
    response = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/section-image/upload`,
      {
        method: "POST",
        credentials: "include",
        body: formData,
      },
    );
  } catch (networkError) {
    // "Failed to fetch" lands here — usually nginx body-size limit, CORS preflight,
    // or the server closed the connection mid-upload.
    throw new Error(
      `Direct upload network error: ${networkError?.message || "Failed to fetch"}. ` +
        `Likely cause: nginx client_max_body_size is below the image size, ` +
        `or CORS rejected the multipart request.`,
    );
  }
  const data = await readJsonOrThrow(response, "Image upload");
  if (!response.ok || !data?.success) {
    throw new Error(data?.message || `Image upload failed (HTTP ${response.status})`);
  }
  const publicUrl = data?.data?.public_url || "";
  const key = data?.data?.key || "";
  if (!publicUrl) {
    throw new Error("Upload completed without a public URL");
  }
  return { public_url: publicUrl, key };
}

async function signedUrlUpload(file) {
  let signedUrlResponse;
  try {
    signedUrlResponse = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/section-image/upload-url`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          file_name: file.name,
          content_type: file.type,
        }),
      },
    );
  } catch (networkError) {
    throw new Error(
      `Signed-URL request network error: ${networkError?.message || "Failed to fetch"}`,
    );
  }
  const signedUrlData = await readJsonOrThrow(signedUrlResponse, "Signed upload");
  if (!signedUrlResponse.ok || !signedUrlData?.success) {
    throw new Error(signedUrlData?.message || "Signed upload unavailable");
  }
  let uploadResponse;
  try {
    uploadResponse = await fetch(signedUrlData?.data?.upload_url, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
  } catch (networkError) {
    // Failed PUT to S3 — usually missing CORS config on the S3 bucket.
    throw new Error(
      `S3 PUT network error: ${networkError?.message || "Failed to fetch"}. ` +
        `Likely cause: the S3 bucket needs CORS config allowing PUT from this admin origin.`,
    );
  }
  if (!uploadResponse.ok) {
    throw new Error(`S3 PUT failed (HTTP ${uploadResponse.status})`);
  }
  const publicUrl = signedUrlData?.data?.public_url || "";
  const key = signedUrlData?.data?.key || "";
  if (!publicUrl) {
    throw new Error("Upload completed without a public URL");
  }
  return { public_url: publicUrl, key };
}

export async function uploadNewsletterImage(file) {
  const validationError = validateNewsletterImage(file);
  if (validationError) {
    throw new Error(validationError);
  }

  // Signed-URL first: browser PUTs straight to S3 (no server hop, no nginx
  // proxy timeout, no 5MB server-memory buffer). Direct-multer upload was
  // reliably timing out because the server → S3 path is slow enough that
  // S3 idle-closes the socket before the PUT finishes. Direct is now only
  // the last-ditch fallback for the case where the S3 bucket lacks a CORS
  // rule for the admin origin.
  try {
    return await signedUrlUpload(file);
  } catch (signedError) {
    try {
      return await directUpload(file);
    } catch (directError) {
      throw new Error(
        `Upload failed.\n• Signed URL: ${signedError?.message || "unknown"}\n• Direct fallback: ${directError?.message || "unknown"}`,
      );
    }
  }
}

